import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { MapContainer, CircleMarker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import {
  ArrowLeft,
  Activity,
  CalendarDays,
  Clock,
  MapPin,
  ShieldCheck,
  Building2,
  Mail,
  Phone,
  Hash,
  Gauge,
  Search,
  Sun,
  Moon,
} from "lucide-react";
import {
  fetchOfficerActivity,
  type OfficerDetail,
  type OfficerActivity,
  type OfficerStatus,
} from "../../services/officers";
import { fetchScanMap, type ScanMapPoint } from "../../services/scanMap";
import { useTranslation } from "../../i18n";
import { cn } from "../../lib/utils";
import { MapStylePicker, MapTiles, useMapStylePref } from "../../components/map/mapStyles";

/** Officer-mode gradient: oldest scan blue (hue 217) → newest red (hue 0). */
function timeGradientColor(ts: number, min: number, max: number): string {
  const fraction = Math.min(1, Math.max(0, (ts - min) / Math.max(max - min, 1)));
  return `hsl(${Math.round(217 * (1 - fraction))}, 82%, 52%)`;
}

const ATLANTA_CENTER: [number, number] = [33.79, -84.38];

/**
 * Selectable time windows for the "Where … Scanned" map. The chosen window
 * drives the scan-map fetch (startDate), the timeline beneath the map, and
 * the scan-history table — all three always show the same window.
 */
const MAP_RANGE_OPTIONS: { days: number; label: string }[] = [
  { days: 1, label: "24h" },
  { days: 3, label: "3d" },
  { days: 7, label: "7d" },
  { days: 30, label: "30d" },
  { days: 90, label: "90d" },
];

/** Human label for a range ("24 hours" / "N days") used in captions. */
function rangeLabel(days: number): string {
  return days === 1 ? "24 hours" : `${days} days`;
}

/** A custom absolute window: local datetime strings from <input type="datetime-local">. */
interface CustomWindow {
  start: string;
  end: string;
}

/** Format a datetime-local value for captions ("Aug 17, 9:00 AM"). */
function fmtLocal(dt: string, lang: string): string {
  const d = new Date(dt);
  if (Number.isNaN(d.getTime())) return dt;
  return new Intl.DateTimeFormat(lang, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(d);
}

/** Default draft for the custom picker: the last 24 hours, minute precision. */
function defaultCustomDraft(): CustomWindow {
  const toLocalInput = (d: Date) => {
    const p = (n: number) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
  };
  const end = new Date();
  const start = new Date(end.getTime() - 24 * 60 * 60 * 1000);
  return { start: toLocalInput(start), end: toLocalInput(end) };
}

/** Segmented control for the map's time window, incl. a Custom day+time mode. */
function MapRangePicker({
  value,
  customActive,
  onChange,
  onCustomToggle,
  disabled,
}: {
  value: number;
  customActive: boolean;
  onChange: (days: number) => void;
  onCustomToggle: () => void;
  disabled?: boolean;
}) {
  return (
    <div
      className="flex items-center overflow-hidden rounded-lg border border-gray-200 bg-white text-xs font-bold dark:border-[#404040] dark:bg-[#2D2D2D]"
      role="group"
      aria-label="Map time range"
    >
      {MAP_RANGE_OPTIONS.map((opt) => (
        <button
          key={opt.days}
          type="button"
          disabled={disabled}
          onClick={() => onChange(opt.days)}
          className={cn(
            "px-2.5 py-1.5 transition-colors",
            opt.days === value && !customActive
              ? "bg-[#3B7BBE] text-white"
              : "text-gray-600 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-[#242424]",
            disabled && "opacity-60"
          )}
          aria-pressed={opt.days === value && !customActive}
        >
          {opt.label}
        </button>
      ))}
      <button
        type="button"
        disabled={disabled}
        onClick={onCustomToggle}
        className={cn(
          "px-2.5 py-1.5 transition-colors",
          customActive
            ? "bg-[#3B7BBE] text-white"
            : "text-gray-600 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-[#242424]",
          disabled && "opacity-60"
        )}
        aria-pressed={customActive}
      >
        Custom
      </button>
    </div>
  );
}

/** Fit the map to the officer's scan points whenever they change. */
function FitBounds({ points }: { points: ScanMapPoint[] }) {
  const map = useMap();
  useEffect(() => {
    if (points.length === 0) return;
    const bounds = L.latLngBounds(points.map((p) => [p.gps_lat, p.gps_lng] as [number, number]));
    if (bounds.isValid()) map.fitBounds(bounds, { padding: [40, 40], maxZoom: 17 });
  }, [points, map]);
  return null;
}

const STATUS_STYLES: Record<OfficerStatus, string> = {
  ACTIVE: "bg-[#A1BF8C]/20 text-[#4F8B3D] dark:text-[#A1BF8C]",
  PENDING: "bg-[#F5B11A]/15 text-[#B4790A] dark:text-[#F5B11A]",
  INACTIVE: "bg-gray-100 text-gray-500 dark:bg-[#33334d] dark:text-gray-400",
  SUSPENDED: "bg-[#EA6D24]/15 text-[#C2540F] dark:text-[#EA6D24]",
  REJECTED: "bg-[#EE3E2E]/10 text-[#EE3E2E]",
};

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? "")
    .join("");
}

function relativeTime(iso: string | null, lang: string): string {
  if (!iso) return "—";
  const then = new Date(iso).getTime();
  const diffMs = Date.now() - then;
  const mins = Math.round(diffMs / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Intl.DateTimeFormat(lang, { month: "short", day: "numeric", year: "numeric" }).format(new Date(iso));
}

/** Build a continuous day-by-day series over the window so the trend has no
 *  gaps. Runs from range_start to range_end (falling back to the last
 *  rangeDays ending today), capped at ~185 buckets for very long windows. */
function buildDailySeries(
  daily: { day: string; count: number }[],
  startIso?: string,
  endIso?: string,
  rangeDays = 30
) {
  const counts = new Map(daily.map((d) => [d.day, d.count]));
  const out: { day: string; label: string; count: number }[] = [];
  const end = endIso ? new Date(endIso) : new Date();
  const start = startIso ? new Date(startIso) : new Date(end.getTime() - (rangeDays - 1) * 24 * 60 * 60 * 1000);
  const cursor = new Date(start);
  cursor.setHours(0, 0, 0, 0);
  const endDay = new Date(end);
  endDay.setHours(0, 0, 0, 0);
  let guard = 0;
  while (cursor <= endDay && guard++ < 185) {
    // en-CA renders YYYY-MM-DD, matching the server's day keys.
    const key = cursor.toLocaleDateString("en-CA");
    out.push({
      day: key,
      label: cursor.toLocaleDateString(undefined, { month: "short", day: "numeric" }),
      count: counts.get(key) ?? 0,
    });
    cursor.setDate(cursor.getDate() + 1);
  }
  return out;
}

function KpiTile({
  icon,
  label,
  value,
  sub,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  sub?: string;
}) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-[#33334d] dark:bg-[#24243e]">
      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
        {icon}
        {label}
      </div>
      <div className="mt-2 text-2xl font-bold text-gray-900 dark:text-white">{value}</div>
      {sub && <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{sub}</div>}
    </div>
  );
}

/** Lightweight SVG bar chart — brand blue bars, no external chart lib. */
function ActivityBarChart({ series }: { series: { day: string; label: string; count: number }[] }) {
  const max = Math.max(1, ...series.map((s) => s.count));
  const W = 720;
  const H = 200;
  const padL = 28;
  const padB = 22;
  const padT = 8;
  const innerW = W - padL;
  const innerH = H - padB - padT;
  const barGap = 2;
  const barW = innerW / series.length - barGap;
  const ticks = 3;
  return (
    <div className="w-full overflow-x-auto">
      <svg viewBox={`0 0 ${W} ${H}`} className="min-w-[560px] w-full" role="img" aria-label="Scans per day">
        {Array.from({ length: ticks + 1 }).map((_, i) => {
          const val = Math.round((max / ticks) * i);
          const y = padT + innerH - (innerH * i) / ticks;
          return (
            <g key={i}>
              <line x1={padL} y1={y} x2={W} y2={y} className="stroke-gray-200 dark:stroke-[#33334d]" strokeWidth={1} />
              <text x={0} y={y + 3} className="fill-gray-400 text-[9px]">
                {val}
              </text>
            </g>
          );
        })}
        {series.map((s, i) => {
          const h = (innerH * s.count) / max;
          const x = padL + i * (barW + barGap);
          const y = padT + innerH - h;
          return (
            <rect key={s.day} x={x} y={y} width={Math.max(1, barW)} height={h} rx={2} className="fill-[#3B7BBE]">
              <title>{`${s.label}: ${s.count} scan${s.count === 1 ? "" : "s"}`}</title>
            </rect>
          );
        })}
        {series.map((s, i) =>
          i % Math.ceil(series.length / 8) === 0 ? (
            <text
              key={`lbl-${s.day}`}
              x={padL + i * (barW + barGap)}
              y={H - 6}
              className="fill-gray-400 text-[9px]"
            >
              {s.label}
            </text>
          ) : null
        )}
      </svg>
    </div>
  );
}

/**
 * Interactive scan timeline. Spans the full width beneath the map: one dot per
 * scan, positioned by its time (oldest left → newest right) and colored with the
 * same gradient as the map markers. Hovering (or scrubbing) the timeline lifts
 * the nearest scan and highlights it on the map, and vice-versa.
 */
function ScanTimeline({
  points,
  min,
  max,
  hoveredId,
  onHover,
  lang,
}: {
  points: ScanMapPoint[];
  min: number;
  max: number;
  hoveredId: string | null;
  onHover: (id: string | null) => void;
  lang: string;
}) {
  const { t } = useTranslation();
  const trackRef = useRef<HTMLDivElement>(null);
  const sorted = useMemo(
    () => [...points].sort((a, b) => Date.parse(a.scanned_at) - Date.parse(b.scanned_at)),
    [points]
  );
  const frac = (ts: number) => Math.min(1, Math.max(0, (ts - min) / Math.max(max - min, 1)));

  const handleMove = (e: React.MouseEvent) => {
    const el = trackRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    let best: ScanMapPoint | null = null;
    let bestD = Infinity;
    for (const p of sorted) {
      const d = Math.abs(frac(Date.parse(p.scanned_at)) - x);
      if (d < bestD) {
        bestD = d;
        best = p;
      }
    }
    onHover(best ? best.id : null);
  };

  const hovered = hoveredId ? sorted.find((p) => p.id === hoveredId) ?? null : null;
  const fmt = (d: Date, withDate = true) =>
    new Intl.DateTimeFormat(lang, {
      ...(withDate ? { month: "short", day: "numeric" } : {}),
      hour: "numeric",
      minute: "2-digit",
    }).format(d);

  // 5 evenly spaced axis ticks across the window.
  const axis = Array.from({ length: 5 }, (_, i) => min + ((max - min) * i) / 4);
  // left position inside the padded track (0.75rem inset on each side).
  const leftFor = (f: number) => `calc(0.75rem + ${f} * (100% - 1.5rem))`;

  return (
    <div className="mt-3 select-none">
      <div className="mb-1 flex items-center justify-between text-[11px] font-semibold text-gray-500 dark:text-gray-400">
        <span>
          {t("officerDetail.timelineRange", "{n} scans · oldest → newest").replace("{n}", String(sorted.length))}
        </span>
        {hovered ? (
          <span className="truncate pl-3 text-right text-[#3B7BBE]">
            {hovered.location_name} · {fmt(new Date(hovered.scanned_at))}
          </span>
        ) : (
          <span className="text-gray-400">{t("officerDetail.hoverTimeline", "Hover the timeline to trace a scan")}</span>
        )}
      </div>
      <div
        ref={trackRef}
        onMouseMove={handleMove}
        onMouseLeave={() => onHover(null)}
        className="relative h-12 cursor-crosshair rounded-lg border border-gray-200 bg-gray-50 dark:border-[#33334d] dark:bg-[#1f1f38]"
      >
        {/* gradient reference bar */}
        <div
          className="pointer-events-none absolute inset-x-3 top-1/2 h-1 -translate-y-1/2 rounded"
          style={{
            background:
              "linear-gradient(to right, hsl(217,82%,52%), hsl(150,82%,48%), hsl(45,90%,52%), hsl(0,82%,52%))",
          }}
        />
        {/* hovered vertical guide */}
        {hovered && (
          <div
            className="pointer-events-none absolute bottom-1 top-1 w-px bg-[#3B7BBE]/50"
            style={{ left: leftFor(frac(Date.parse(hovered.scanned_at))) }}
          />
        )}
        {/* per-scan dots — the hovered dot is skipped here and drawn separately
            below so it is the LAST element in the track: painted on top of every
            neighbor (dots can overlap when scans cluster in time), and lifted with
            a high z-index, so hovering always brings that scan to the foreground. */}
        {sorted.map((p) => {
          if (p.id === hoveredId) return null;
          const f = frac(Date.parse(p.scanned_at));
          return (
            <div
              key={p.id}
              className="pointer-events-none absolute top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full"
              style={{
                left: leftFor(f),
                width: 7,
                height: 7,
                backgroundColor: timeGradientColor(Date.parse(p.scanned_at), min, max),
                border: "1px solid rgba(255,255,255,0.7)",
                zIndex: 1,
              }}
            />
          );
        })}
        {hovered && (
          <div
            className="pointer-events-none absolute top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full"
            style={{
              left: leftFor(frac(Date.parse(hovered.scanned_at))),
              width: 14,
              height: 14,
              backgroundColor: timeGradientColor(Date.parse(hovered.scanned_at), min, max),
              border: "2px solid #fff",
              boxShadow: "0 0 0 2px rgba(59,123,190,0.65)",
              zIndex: 30,
            }}
          />
        )}
      </div>
      {/* time axis */}
      <div className="relative mt-1 h-4 text-[10px] text-gray-400">
        {axis.map((ts, i) => (
          <span
            key={i}
            className={cn(
              "absolute whitespace-nowrap",
              i === 0 ? "translate-x-0" : i === axis.length - 1 ? "-translate-x-full" : "-translate-x-1/2"
            )}
            style={{ left: leftFor(i / (axis.length - 1)) }}
          >
            {fmt(new Date(ts), true)}
          </span>
        ))}
      </div>
      <p className="mt-2 max-w-2xl text-[11px] leading-snug text-gray-400">
        {t("officerDetail.timelineCaption", "Each dot is one scan, placed by when it happened and colored oldest-blue to newest-red. Hover to highlight it on the map. No path between scans is drawn — only recorded scan locations are shown.")}
      </p>
    </div>
  );
}

/**
 * Searchable scan-history table — the officer's recorded scans in list form,
 * newest first, filterable by location, hospital, date/time, or coordinates.
 */
function ScanHistoryTable({ points, lang, windowLabel }: { points: ScanMapPoint[]; lang: string; windowLabel: string }) {
  const { t } = useTranslation();
  const [q, setQ] = useState("");
  const rows = useMemo(
    () => [...points].sort((a, b) => Date.parse(b.scanned_at) - Date.parse(a.scanned_at)),
    [points]
  );
  const fmtDate = (iso: string) =>
    new Intl.DateTimeFormat(lang, {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      second: "2-digit",
    }).format(new Date(iso));
  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter(
      (r) =>
        (r.location_name || "").toLowerCase().includes(term) ||
        (r.hospital_name || "").toLowerCase().includes(term) ||
        fmtDate(r.scanned_at).toLowerCase().includes(term) ||
        `${r.gps_lat?.toFixed(5)}, ${r.gps_lng?.toFixed(5)}`.includes(term)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, q]);

  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm dark:border-[#33334d] dark:bg-[#24243e]">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <h2 className="flex items-center gap-2 text-base font-bold text-gray-900 dark:text-white">
          <Activity className="h-4 w-4 text-[#3B7BBE]" />
          {t("officerDetail.scanHistory", "Scan History")}
        </h2>
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t("officerDetail.searchScans", "Search scans…")}
            className="w-56 rounded-lg border border-gray-300 bg-white py-1.5 pl-8 pr-3 text-sm text-gray-900 placeholder-gray-400 focus:border-[#3B7BBE] focus:outline-none focus:ring-1 focus:ring-[#3B7BBE] dark:border-[#33334d] dark:bg-[#1f1f38] dark:text-white"
          />
        </div>
      </div>
      <div className="max-h-[420px] overflow-auto rounded-lg border border-gray-200 dark:border-[#33334d]">
        <table className="w-full text-left text-sm">
          <thead className="sticky top-0 z-10 bg-gray-50 text-xs uppercase tracking-wide text-gray-500 dark:bg-[#1f1f38] dark:text-gray-400">
            <tr>
              <th className="px-3 py-2 font-semibold">{t("officerDetail.colDateTime", "Date & Time")}</th>
              <th className="px-3 py-2 font-semibold">{t("officerDetail.colLocation", "Location")}</th>
              <th className="px-3 py-2 font-semibold">{t("officerDetail.colHospital", "Hospital")}</th>
              <th className="px-3 py-2 font-semibold">{t("officerDetail.colCoordinates", "Coordinates")}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-[#383838]">
            {filtered.map((r) => (
              <tr key={r.id} className="hover:bg-gray-50 dark:hover:bg-[#2a2a47]">
                <td className="whitespace-nowrap px-3 py-2 text-gray-700 dark:text-gray-200">{fmtDate(r.scanned_at)}</td>
                <td className="px-3 py-2 text-gray-900 dark:text-white">{r.location_name}</td>
                <td className="px-3 py-2 text-gray-700 dark:text-gray-300">{r.hospital_name}</td>
                <td className="whitespace-nowrap px-3 py-2 font-mono text-xs text-gray-500 dark:text-gray-400">
                  {r.gps_lat != null && r.gps_lng != null ? `${r.gps_lat.toFixed(5)}, ${r.gps_lng.toFixed(5)}` : "—"}
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={4} className="px-3 py-8 text-center text-sm text-gray-400">
                  {t("officerDetail.noScansMatch", "No scans match “{q}”.").replace("{q}", q)}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <p className="mt-2 text-[11px] text-gray-400">
        {t("officerDetail.showingScansWindow", "Showing {shown} of {total} recorded scans ({range}).")
          .replace("{shown}", filtered.length.toLocaleString(lang))
          .replace("{total}", rows.length.toLocaleString(lang))
          .replace("{range}", windowLabel)}
      </p>
    </div>
  );
}

export function OfficerDetailPage() {
  const { id = "" } = useParams();
  const { t, lang } = useTranslation();

  const [officer, setOfficer] = useState<OfficerDetail | null>(null);
  const [activity, setActivity] = useState<OfficerActivity | null>(null);
  const [points, setPoints] = useState<ScanMapPoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Shared hover state linking the timeline and the map, in both directions.
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  // Map basemap + light/dark tiles, same controls as the main Scan Map.
  const [mapStyleIndex, setMapStyleIndex] = useMapStylePref("officermap.mapStyle");
  const [mapDark, setMapDark] = useState<boolean>(() => {
    try {
      const s = localStorage.getItem("officermap.tileDark");
      return s == null ? document.documentElement.classList.contains("dark") : s === "1";
    } catch {
      return false;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem("officermap.tileDark", mapDark ? "1" : "0");
    } catch {
      /* ignore storage errors */
    }
  }, [mapDark]);
  // Time window for the scan map / timeline / history table. Remembered
  // across visits; changing it refetches only the map data (not the whole page).
  const [mapRangeDays, setMapRangeDays] = useState<number>(() => {
    try {
      const s = Number(localStorage.getItem("officermap.rangeDays"));
      return MAP_RANGE_OPTIONS.some((o) => o.days === s) ? s : 30;
    } catch {
      return 30;
    }
  });
  const [mapLoading, setMapLoading] = useState(false);
  useEffect(() => {
    try {
      localStorage.setItem("officermap.rangeDays", String(mapRangeDays));
    } catch {
      /* ignore storage errors */
    }
  }, [mapRangeDays]);
  // Custom day+time window. When set, it overrides the preset buttons —
  // e.g. review exactly one shift: Aug 17, 9:00 AM → Aug 17, 12:00 PM.
  const [customRange, setCustomRange] = useState<CustomWindow | null>(null);
  const [customOpen, setCustomOpen] = useState(false);
  const [customDraft, setCustomDraft] = useState<CustomWindow>(defaultCustomDraft);
  const customValid =
    !Number.isNaN(Date.parse(customDraft.start)) &&
    !Number.isNaN(Date.parse(customDraft.end)) &&
    Date.parse(customDraft.start) < Date.parse(customDraft.end);

  // Single windowed fetch: the selected window drives EVERYTHING on the page —
  // KPI tiles, activity chart, scan map, timeline, and history all describe
  // the same start→end range. First visit shows the full-page spinner; window
  // changes only dim the affected sections.
  const initialLoadedRef = useRef(false);
  useEffect(() => {
    let cancelled = false;
    const firstLoad = !initialLoadedRef.current;
    if (firstLoad) setLoading(true);
    setMapLoading(true);
    setError(null);

    const startIso = customRange
      ? new Date(customRange.start).toISOString()
      : new Date(Date.now() - mapRangeDays * 24 * 60 * 60 * 1000).toISOString();
    const endIso = customRange ? new Date(customRange.end).toISOString() : undefined;

    Promise.all([
      fetchOfficerActivity(id, { startDate: startIso, ...(endIso ? { endDate: endIso } : {}) }),
      fetchScanMap({
        officerId: id,
        startDate: startIso,
        ...(endIso ? { endDate: endIso } : {}),
        limit: 5000,
      }).catch(() => ({ scans: [] as ScanMapPoint[] })),
    ])
      .then(([oa, map]) => {
        if (cancelled) return;
        setOfficer(oa.officer);
        setActivity(oa.activity);
        setPoints(map.scans);
        initialLoadedRef.current = true;
      })
      .catch((e) => {
        if (!cancelled) setError(e?.message || t("officerDetail.loadFailed", "Failed to load officer"));
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
          setMapLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [id, mapRangeDays, customRange]);

  // Human label for whatever window is active, used in captions.
  const windowLabel = customRange
    ? `${fmtLocal(customRange.start, lang)} → ${fmtLocal(customRange.end, lang)}`
    : `last ${rangeLabel(mapRangeDays)}`;

  const timeSpan = useMemo(() => {
    if (points.length === 0) return { min: 0, max: 1 };
    let min = Infinity;
    let max = -Infinity;
    for (const p of points) {
      const ts = Date.parse(p.scanned_at);
      if (ts < min) min = ts;
      if (ts > max) max = ts;
    }
    return { min, max: Math.max(max, min + 1) };
  }, [points]);

  const series = useMemo(
    () =>
      activity
        ? buildDailySeries(activity.daily, activity.range_start, activity.range_end, activity.range_days)
        : [],
    [activity]
  );
  const rangeTotal = useMemo(() => series.reduce((a, s) => a + s.count, 0), [series]);
  const hoveredPoint = useMemo(
    () => (hoveredId ? points.find((p) => p.id === hoveredId) ?? null : null),
    [hoveredId, points]
  );

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-[#3B7BBE] border-t-transparent" />
      </div>
    );
  }

  if (error || !officer) {
    return (
      <div className="mx-auto max-w-2xl p-6 text-center">
        <p className="text-gray-600 dark:text-gray-300">{error || t("officerDetail.notFound", "Officer not found.")}</p>
        <Link
          to="/supervisor/officers"
          className="mt-4 inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-semibold text-gray-700 hover:bg-gray-50 dark:border-[#33334d] dark:text-gray-200"
        >
          <ArrowLeft className="h-4 w-4" /> {t("officerDetail.backToOfficers", "Back to Officers")}
        </Link>
      </div>
    );
  }

  // KPI figures follow the selected window (falling back to lifetime totals
  // if the API predates windowed activity).
  const windowScans = activity?.window_scans ?? officer.total_scans;
  const windowShifts = activity?.window_sessions ?? officer.total_sessions;
  const avgPerShift = windowShifts > 0 ? Math.round(windowScans / windowShifts) : null;

  return (
    <div className="mx-auto max-w-6xl space-y-5 p-4 sm:p-6">
      {/* Back link */}
      <Link
        to="/supervisor/officers"
        className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#3B7BBE] hover:underline"
      >
        <ArrowLeft className="h-4 w-4" /> {t("officerDetail.allOfficers", "All Officers")}
      </Link>

      {/* Header card */}
      <div className="flex flex-col gap-4 rounded-2xl border border-gray-200 bg-white p-5 shadow-sm dark:border-[#33334d] dark:bg-[#24243e] sm:flex-row sm:items-center">
        <div className="flex h-16 w-16 flex-shrink-0 items-center justify-center rounded-full bg-[#3B7BBE] text-2xl font-bold text-white">
          {initials(officer.name)}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{officer.name}</h1>
            <span
              className={cn(
                "inline-flex items-center rounded-md px-2 py-0.5 text-xs font-bold uppercase tracking-wide",
                STATUS_STYLES[officer.status]
              )}
            >
              {officer.status}
            </span>
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-500 dark:text-gray-400">
            <span className="inline-flex items-center gap-1 font-mono font-semibold">
              <Hash className="h-3.5 w-3.5" />
              {officer.n_number}
            </span>
            {officer.hospital_name && (
              <span className="inline-flex items-center gap-1">
                <Building2 className="h-3.5 w-3.5" />
                {officer.hospital_name}
              </span>
            )}
            {officer.email && (
              <span className="inline-flex items-center gap-1">
                <Mail className="h-3.5 w-3.5" />
                {officer.email}
              </span>
            )}
            {officer.phone && (
              <span className="inline-flex items-center gap-1">
                <Phone className="h-3.5 w-3.5" />
                {officer.phone}
              </span>
            )}
          </div>
          <div className="mt-1 text-xs text-gray-400">
            {officer.enrolled_via === "SELF" ? t("officerDetail.selfEnrolled", "Self-enrolled") : t("officerDetail.addedBySupervisor", "Added by supervisor")}
            {officer.created_at &&
              ` · ${t("officerDetail.memberSince", "member since {date}").replace("{date}", new Intl.DateTimeFormat(lang, {
                month: "short",
                day: "numeric",
                year: "numeric",
              }).format(new Date(officer.created_at)))}`}
          </div>
        </div>
      </div>

      {/* KPI tiles */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        <KpiTile
          icon={<Activity className="h-3.5 w-3.5" />}
          label={t("officerDetail.scans", "Scans")}
          value={windowScans.toLocaleString(lang)}
          sub={windowLabel}
        />
        <KpiTile
          icon={<ShieldCheck className="h-3.5 w-3.5" />}
          label={t("officerDetail.shifts", "Shifts")}
          value={windowShifts.toLocaleString(lang)}
          sub={windowLabel}
        />
        <KpiTile
          icon={<CalendarDays className="h-3.5 w-3.5" />}
          label={t("officerDetail.activeDays", "Active days")}
          value={(activity?.active_days ?? 0).toLocaleString(lang)}
          sub={windowLabel}
        />
        <KpiTile
          icon={<Gauge className="h-3.5 w-3.5" />}
          label={t("officerDetail.avgShift", "Avg / shift")}
          value={avgPerShift == null ? "—" : avgPerShift.toLocaleString(lang)}
          sub={avgPerShift == null ? t("officerDetail.noShiftsInWindow", "no shifts in window") : t("officerDetail.scansPerShift", "scans per shift")}
        />
        <KpiTile
          icon={<Clock className="h-3.5 w-3.5" />}
          label={t("officerDetail.lastActive", "Last active")}
          value={relativeTime(activity?.last_scan_at ?? null, lang)}
          sub={t("officerDetail.inWindow", "in window")}
        />
      </div>

      {/* Scan map — time-gradient (placed above the activity chart) */}
      <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm dark:border-[#33334d] dark:bg-[#24243e]">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <h2 className="flex items-center gap-2 text-base font-bold text-gray-900 dark:text-white">
            <MapPin className="h-4 w-4 text-[#EE3E2E]" />
            {t("officerDetail.whereScanned", "Where {name} Scanned").replace("{name}", officer.name.split(" ")[0])}
          </h2>
          <div className="flex items-center gap-2">
            {/* Time-window picker — always visible so an empty window can be widened. */}
            <MapRangePicker
              value={mapRangeDays}
              customActive={customRange !== null}
              onChange={(days) => {
                setCustomRange(null);
                setCustomOpen(false);
                setMapRangeDays(days);
              }}
              onCustomToggle={() => setCustomOpen((v) => !v)}
              disabled={mapLoading}
            />
            {points.length > 0 && (
              <>
                <MapStylePicker styleIndex={mapStyleIndex} onChange={setMapStyleIndex} />
                <button
                  type="button"
                  onClick={() => setMapDark((v) => !v)}
                  className="flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-bold text-gray-600 transition-colors hover:bg-gray-50 dark:border-[#404040] dark:bg-[#2D2D2D] dark:text-gray-200 dark:hover:bg-[#242424]"
                  title={mapDark ? t("officerDetail.mapToLight", "Switch map to light tiles") : t("officerDetail.mapToDark", "Switch map to dark tiles")}
                >
                  {mapDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
                  {mapDark ? t("officerDetail.light", "Light") : t("officerDetail.dark", "Dark")}
                </button>
              </>
            )}
          </div>
        </div>
        {/* Custom day + time window: pick an exact start and end (e.g. one
            shift) — applies to the map, timeline, and scan history below. */}
        {customOpen && (
          <div className="mb-3 flex flex-wrap items-end gap-3 rounded-xl border border-gray-200 bg-gray-50 p-3 dark:border-[#33334d] dark:bg-[#1f1f38]">
            <label className="flex flex-col gap-1 text-[11px] font-semibold text-gray-500 dark:text-gray-400">
              {t("officerDetail.customFrom", "From (date & time)")}
              <input
                type="datetime-local"
                value={customDraft.start}
                max={customDraft.end || undefined}
                onChange={(e) => setCustomDraft((d) => ({ ...d, start: e.target.value }))}
                className="rounded-lg border border-gray-300 bg-white px-2.5 py-1.5 text-sm text-gray-900 focus:border-[#3B7BBE] focus:outline-none dark:border-[#33334d] dark:bg-[#24243e] dark:text-white"
              />
            </label>
            <label className="flex flex-col gap-1 text-[11px] font-semibold text-gray-500 dark:text-gray-400">
              {t("officerDetail.customTo", "To (date & time)")}
              <input
                type="datetime-local"
                value={customDraft.end}
                min={customDraft.start || undefined}
                onChange={(e) => setCustomDraft((d) => ({ ...d, end: e.target.value }))}
                className="rounded-lg border border-gray-300 bg-white px-2.5 py-1.5 text-sm text-gray-900 focus:border-[#3B7BBE] focus:outline-none dark:border-[#33334d] dark:bg-[#24243e] dark:text-white"
              />
            </label>
            <button
              type="button"
              disabled={!customValid || mapLoading}
              onClick={() => setCustomRange({ ...customDraft })}
              className={cn(
                "rounded-lg px-4 py-1.5 text-sm font-bold text-white transition-colors",
                customValid ? "bg-[#3B7BBE] hover:bg-[#33699F]" : "cursor-not-allowed bg-gray-300 dark:bg-[#33334d]"
              )}
            >
              {t("officerDetail.customApply", "Apply")}
            </button>
            {customRange && (
              <button
                type="button"
                onClick={() => {
                  setCustomRange(null);
                  setCustomOpen(false);
                }}
                className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-semibold text-gray-600 hover:bg-gray-100 dark:border-[#33334d] dark:text-gray-200 dark:hover:bg-[#2a2a47]"
              >
                {t("officerDetail.customClear", "Clear")}
              </button>
            )}
            {!customValid && (
              <span className="pb-2 text-[11px] text-red-500">
                {t("officerDetail.customInvalid", "Start must be before end.")}
              </span>
            )}
            {customRange && (
              <span className="pb-2 text-[11px] font-semibold text-[#3B7BBE]">
                {t("officerDetail.customShowing", "Showing:")} {windowLabel}
              </span>
            )}
          </div>
        )}
        {points.length > 0 ? (
          <>
            <div className={cn(
              "relative overflow-hidden rounded-xl border border-gray-200 transition-opacity dark:border-[#33334d]",
              mapLoading && "opacity-60"
            )}>
              <MapContainer attributionControl={false}
                center={ATLANTA_CENTER}
                zoom={11}
                style={{ height: "380px", width: "100%" }}
                preferCanvas
              >
                <MapTiles styleIndex={mapStyleIndex} isDark={mapDark} />
                <FitBounds points={points} />
                {/* Highlight ring for the hovered scan — drawn first so the dot sits on top. */}
                {hoveredPoint && (
                  <CircleMarker
                    center={[hoveredPoint.gps_lat, hoveredPoint.gps_lng]}
                    radius={17}
                    interactive={false}
                    pathOptions={{ color: "#3B7BBE", weight: 2, fillColor: "#3B7BBE", fillOpacity: 0.12 }}
                  />
                )}
                {points.map((p) => {
                  const active = p.id === hoveredId;
                  return (
                  <CircleMarker
                    key={p.id}
                    center={[p.gps_lat, p.gps_lng]}
                    radius={active ? 11 : 7}
                    pathOptions={{
                      color: active ? "#3B7BBE" : "#333333",
                      fillColor: timeGradientColor(Date.parse(p.scanned_at), timeSpan.min, timeSpan.max),
                      fillOpacity: 0.9,
                      weight: active ? 3 : 2,
                    }}
                    eventHandlers={{
                      mouseover: () => setHoveredId(p.id),
                      mouseout: () => setHoveredId(null),
                    }}
                  >
                    <Popup>
                      <div className="space-y-1 text-xs">
                        <div className="text-sm font-bold">{p.location_name}</div>
                        <div>
                          <span className="font-bold">{t("map.popupHospital", "Hospital:")}</span> {p.hospital_name}
                        </div>
                        <div>
                          <span className="font-bold">{t("map.popupTime", "Time:")}</span>{" "}
                          {new Intl.DateTimeFormat(lang, {
                            month: "short",
                            day: "numeric",
                            hour: "numeric",
                            minute: "2-digit",
                            second: "2-digit",
                          }).format(new Date(p.scanned_at))}
                        </div>
                      </div>
                    </Popup>
                  </CircleMarker>
                  );
                })}
              </MapContainer>
            </div>
            {/* Interactive time-gradient timeline (replaces the static legend) */}
            <ScanTimeline
              points={points}
              min={timeSpan.min}
              max={timeSpan.max}
              hoveredId={hoveredId}
              onHover={setHoveredId}
              lang={lang}
            />
          </>
        ) : mapLoading ? (
          <div className="flex h-40 items-center justify-center">
            <div className="h-6 w-6 animate-spin rounded-full border-4 border-[#3B7BBE] border-t-transparent" />
          </div>
        ) : (
          <p className="py-8 text-center text-sm text-gray-400">
            {t("officerDetail.noGpsScansWindow", "No GPS-tagged scans to map in this window ({range}). Try a wider time window above.")
              .replace("{range}", windowLabel)}
          </p>
        )}
      </div>

      {/* Scan activity over time */}
      <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm dark:border-[#33334d] dark:bg-[#24243e]">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="flex items-center gap-2 text-base font-bold text-gray-900 dark:text-white">
            <Activity className="h-4 w-4 text-[#3B7BBE]" />
            {t("officerDetail.scanActivityWindow", "Scan Activity — {range}").replace("{range}", windowLabel)}
          </h2>
          <span className="text-xs font-semibold text-gray-500 dark:text-gray-400">
            {rangeTotal.toLocaleString(lang)} {t("officerMap.scans", "scans")}
          </span>
        </div>
        {rangeTotal > 0 ? (
          <ActivityBarChart series={series} />
        ) : (
          <p className="py-8 text-center text-sm text-gray-400">{t("officerDetail.noScansInWindow", "No scans recorded in this window ({range}).").replace("{range}", windowLabel)}</p>
        )}
      </div>

      {/* Scan history — searchable list (same time window as the map) */}
      {points.length > 0 && <ScanHistoryTable points={points} lang={lang} windowLabel={windowLabel} />}
    </div>
  );
}

export default OfficerDetailPage;
